/**
 * Open Bank Accounts List
 *
 * Displays connected bank accounts from TrueLayer with balances.
 */

import React, { useState } from 'react';
import {
  useBankAccounts,
  useSyncBankConnection,
  useUpdateBankAccount,
  useDisconnectBankConnection,
  formatBankAmount,
  getBankAccountDisplayName,
  type OBAccount,
} from '@business-assistant/shared';

interface OpenBankAccountsListProps {
  /** Business account ID */
  accountId: string;
  /** Callback when an account is selected */
  onSelectAccount?: (account: OBAccount) => void;
  /** Show sync button */
  showSync?: boolean;
  /** Show disconnect button */
  showDisconnect?: boolean;
  /** Compact mode */
  compact?: boolean;
}

export function OpenBankAccountsList({
  accountId,
  onSelectAccount,
  showSync = true,
  showDisconnect = false,
  compact = false,
}: OpenBankAccountsListProps) {
  const [syncingConnectionId, setSyncingConnectionId] = useState<string | null>(null);

  const { data: accounts = [], isLoading, refetch } = useBankAccounts(accountId);
  const syncConnection = useSyncBankConnection();
  const updateAccount = useUpdateBankAccount();
  const disconnectConnection = useDisconnectBankConnection();

  const handleSync = async (connectionId: string) => {
    setSyncingConnectionId(connectionId);
    try {
      await syncConnection.mutateAsync({
        connectionId,
        fetchTransactions: true,
      });
      refetch();
    } finally {
      setSyncingConnectionId(null);
    }
  };

  const handleToggleFavorite = async (account: OBAccount) => {
    await updateAccount.mutateAsync({
      bankAccountId: account.id,
      isFavorite: !account.isFavorite,
    });
    refetch();
  };

  const handleDisconnect = async (connectionId: string) => {
    if (!confirm('Sei sicuro di voler scollegare questo conto? Tutti i dati verranno eliminati.')) {
      return;
    }

    await disconnectConnection.mutateAsync({
      connectionId,
      revokeToken: true,
    });
    refetch();
  };

  // Group accounts by connection
  const accountsByConnection = accounts.reduce<Record<string, OBAccount[]>>(
    (acc, account) => {
      if (!acc[account.connectionId]) {
        acc[account.connectionId] = [];
      }
      acc[account.connectionId].push(account);
      return acc;
    },
    {}
  );

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          style={{ animation: 'spin 1s linear infinite', margin: '0 auto' }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        <p style={{ marginTop: '8px' }}>Caricamento conti...</p>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div
        style={{
          padding: '32px',
          textAlign: 'center',
          color: '#6b7280',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          style={{ opacity: 0.5, margin: '0 auto 12px' }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
          />
        </svg>
        <p>Nessun conto Open Banking collegato</p>
        <p style={{ fontSize: '13px', marginTop: '4px' }}>
          Collega il tuo conto bancario per sincronizzare automaticamente saldo e movimenti.
        </p>
      </div>
    );
  }

  // Calculate total balance
  const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Total Balance Header */}
      {!compact && (
        <div
          style={{
            padding: '16px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
          }}
        >
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
            Saldo totale Open Banking
          </div>
          <div style={{ fontSize: '24px', fontWeight: 600, color: '#111827' }}>
            {formatBankAmount(totalBalance)}
          </div>
        </div>
      )}

      {/* Accounts List */}
      {Object.entries(accountsByConnection).map(([connectionId, connectionAccounts]) => (
        <div
          key={connectionId}
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
          }}
        >
          {/* Connection Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
                />
              </svg>
              <span style={{ fontWeight: 500 }}>
                {connectionAccounts[0]?.bankName || 'Banca collegata'}
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 500,
                  backgroundColor: '#dcfce7',
                  color: '#16a34a',
                }}
              >
                Open Banking
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {showSync && (
                <button
                  onClick={() => handleSync(connectionId)}
                  disabled={syncingConnectionId === connectionId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    backgroundColor: 'transparent',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: syncingConnectionId === connectionId ? 'not-allowed' : 'pointer',
                  }}
                  title="Sincronizza"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    style={{
                      animation: syncingConnectionId === connectionId ? 'spin 1s linear infinite' : 'none',
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Sync
                </button>
              )}

              {showDisconnect && (
                <button
                  onClick={() => handleDisconnect(connectionId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    backgroundColor: 'transparent',
                    border: '1px solid #fca5a5',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#dc2626',
                    cursor: 'pointer',
                  }}
                  title="Scollega"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Account Items */}
          {connectionAccounts.map((account) => (
            <div
              key={account.id}
              onClick={() => onSelectAccount?.(account)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                cursor: onSelectAccount ? 'pointer' : 'default',
                borderBottom: '1px solid #e5e7eb',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => {
                if (onSelectAccount) {
                  (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f9fafb';
                }
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Account Icon */}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    backgroundColor: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {account.accountType === 'CREDIT_CARD' ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
                      />
                    </svg>
                  )}
                </div>

                {/* Account Info */}
                <div>
                  <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {getBankAccountDisplayName(account)}
                    {account.isFavorite && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#eab308" stroke="#eab308">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {account.iban || account.accountNumber || account.accountType}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Balance */}
                <div style={{ textAlign: 'right' }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: account.balance >= 0 ? '#111827' : '#dc2626',
                    }}
                  >
                    {formatBankAmount(account.balance, account.currency)}
                  </div>
                  {account.availableBalance != null && account.availableBalance !== account.balance && (
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                      Disponibile: {formatBankAmount(account.availableBalance, account.currency)}
                    </div>
                  )}
                </div>

                {/* Favorite Toggle */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFavorite(account);
                  }}
                  style={{
                    padding: '4px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  title={account.isFavorite ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                >
                  {account.isFavorite ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#eab308" stroke="#eab308">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      />
                    </svg>
                  )}
                </button>

                {/* Arrow */}
                {onSelectAccount && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default OpenBankAccountsList;
